'use client';

import {
  type ChangeEvent,
  type ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';

import { useAppMediaPermission } from '@ps/functions/native-bridge/useAppMediaPermission';

import type {
  FileInfo,
  InvalidFileInfo,
  LocalFileInfo,
} from '../types/FileInfo';
import type { FileUploadStatus } from '../types/FileUploadStatus';
import type { FileUploaderContentProps } from '../types/FileUploaderContentProps';
import type { SupportedFileFormats } from '../types/SupportedFileFormats';
import type { UploadFileFn } from '../types/UploadFileFn';

// ─── Default size caps ──────────────────────────────────────────────────────

const kDefaultMaxSizeMB = 1024;
const kDefaultMaxImageSizeMB = 20;
const kDefaultMaxVideoSizeMB = 1024;

// ─── Props ──────────────────────────────────────────────────────────────────

interface FileUploaderCommonProps {
  /** Generic size cap (MB). Used for files that aren't image/video. */
  maxSizeInMB?: number;
  /** Image-specific size cap (MB). */
  maxImageSizeInMB?: number;
  /** Video-specific size cap (MB). */
  maxVideoSizeInMB?: number;
  /** Allowed format → MIME map. Omit to skip MIME validation. */
  supportedFormats?: SupportedFileFormats;
  /** Render prop. Receives the derived content props. */
  children: (props: FileUploaderContentProps) => ReactElement;
  /** Fired with any throw from the picker / validation / upload pipeline. */
  onError?: (error: unknown) => void;
}

interface FileUploaderSingleProps extends FileUploaderCommonProps {
  multiple?: false;
  value?: FileInfo;
  /**
   * Injected upload function — required when `uploadFileOnChange` is
   * not `false`. Headless on purpose: the component doesn't know
   * whether you're uploading to Firebase Storage, the media-gallery
   * presigned-URL pipeline, or some other backend.
   */
  uploadFile?: UploadFileFn;
  /**
   * Set to `false` to keep files local — useful for "Pick now, upload
   * on submit" flows. Defaults to `true`.
   */
  uploadFileOnChange?: boolean;
  /** Forwarded to {@link UploadFileFn} as `options.directoryPath`. */
  uploadDirectoryPath?: string;
  /** Fires through the upload lifecycle. */
  onUploadStatusChange?: (status: FileUploadStatus) => void;
  /** Fires when the bound value changes (after picking or after upload). */
  onChange?: (value: FileInfo | undefined) => void;
}

interface FileUploaderMultipleProps extends FileUploaderCommonProps {
  multiple: true;
  value?: FileInfo[];
  /**
   * Fires through the upload lifecycle. Multi-mode doesn't auto-upload
   * here — consumer drives uploads per-file post-pick.
   */
  onUploadStatusChange?: (status: FileUploadStatus) => void;
  /**
   * Fires after picking. `invalidFiles` carries per-file validation
   * errors so the UI can render mixed-success rows.
   */
  onChange?: (
    value: FileInfo[] | undefined,
    invalidFiles: InvalidFileInfo[],
  ) => void;
}

export type FileUploaderProps =
  | FileUploaderSingleProps
  | FileUploaderMultipleProps;

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Headless file picker + validation + (optional) upload. Mirrors
 * PopshopRN's `appComponents/file-uploader/FileUploader` — translated
 * to web (no RN bridge, no `Platform.OS` checks, no
 * `ReactNativeWebView` postMessage).
 *
 * Use it for any "pick a file and do something with it" UI:
 *   - Channel icon: image + cropper (compose with `ImageCropperDialog`)
 *   - Digital product: PDF / ZIP → Firebase Storage via `uploadFileAsync`
 *   - Post media: image / video → media-gallery via an `uploadMediaItem` adapter
 *
 * Always renders a hidden `<input type="file">` — pickers can't be
 * unmounted between renders or browsers lose the file selection event.
 *
 * The component is headless: pass `children(props)` and render
 * whatever visual fits the use case.
 */
export function FileUploader(props: FileUploaderProps): ReactElement {
  const {
    maxSizeInMB = kDefaultMaxSizeMB,
    maxImageSizeInMB = kDefaultMaxImageSizeMB,
    maxVideoSizeInMB = kDefaultMaxVideoSizeMB,
    supportedFormats,
    children,
    onError,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  // `inputKey` resets the file input so picking the same file twice
  // still fires a `change` event (the input would otherwise no-op).
  const [inputKey, setInputKey] = useState(0);

  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[] | undefined>(
    () => {
      if (props.multiple) return props.value;
      return props.value ? [props.value] : undefined;
    },
  );

  const validateFile = useCallback(
    (file: File): string | undefined => {
      if (supportedFormats != null) {
        const mimeType = file.type.toLowerCase();
        const isValidFormat = Object.values(supportedFormats).some(types =>
          types.includes(mimeType),
        );
        if (!isValidFormat) {
          const accepted = Object.keys(supportedFormats)
            .map(key => key.toUpperCase())
            .join('/');
          return `Only ${accepted} files are supported.`;
        }
      }

      const maxSize = file.type.startsWith('image/')
        ? maxImageSizeInMB
        : file.type.startsWith('video/')
          ? maxVideoSizeInMB
          : maxSizeInMB;

      const sizeMB = file.size / 1024 / 1024;
      if (sizeMB > maxSize) {
        return `File size must be smaller than ${formatSize(maxSize)}.`;
      }

      return undefined;
    },
    [maxImageSizeInMB, maxSizeInMB, maxVideoSizeInMB, supportedFormats],
  );

  // ── Bridge consumer callbacks ─────────────────────────────────────────

  const commitFiles = useCallback(
    (next: FileInfo[] | undefined, invalidFiles: InvalidFileInfo[]) => {
      setUploadedFiles(next);
      if (props.multiple) {
        props.onChange?.(next, invalidFiles);
      } else {
        props.onChange?.(next?.[0]);
      }
    },
    [props],
  );

  // ── Open native picker ────────────────────────────────────────────────

  const { ensureNativePermission } = useAppMediaPermission();

  const onClick = useCallback(() => {
    // Fire-and-forget BEFORE the click — awaiting would drop the user gesture
    // and the file dialog wouldn't open. No-op on the web.
    ensureNativePermission();
    inputRef.current?.click();
  }, [ensureNativePermission]);

  // ── Handle file selection ─────────────────────────────────────────────

  const onFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      try {
        const picked = props.multiple
          ? event.target.files
            ? Array.from(event.target.files)
            : []
          : event.target.files?.length
            ? [event.target.files[0]]
            : [];

        if (picked.length === 0) return;

        const invalidFiles: InvalidFileInfo[] = [];
        const localFiles: LocalFileInfo[] = [];

        for (const file of picked) {
          const errorMsg = validateFile(file);
          if (errorMsg) {
            if (props.multiple) {
              invalidFiles.push({ file, errorMsg });
              continue;
            }
            // Single-mode validation failure throws so it surfaces
            // through `onError` rather than silently dropping the pick.
            throw new Error(errorMsg);
          }

          localFiles.push({
            filename: file.name,
            size: file.size,
            type: file.type,
            source: 'local',
            localUri: URL.createObjectURL(file),
            localFile: file,
          });
        }

        if (props.multiple) {
          commitFiles(localFiles, invalidFiles);
          return;
        }

        // ── Single-file branch ────────────────────────────────────────
        const localFile = localFiles[0];

        // Surface the local pick first so the UI can switch to the
        // "uploading" state before the network round-trip starts.
        commitFiles(localFiles, invalidFiles);

        if (props.uploadFileOnChange === false) return;

        if (!props.uploadFile) {
          throw new Error(
            'FileUploader: `uploadFile` is required when `uploadFileOnChange` is not `false`.',
          );
        }

        props.onUploadStatusChange?.({
          type: 'uploading',
          progress: 0,
          file: localFile,
        });

        try {
          const uri = await props.uploadFile(localFile, {
            directoryPath: props.uploadDirectoryPath,
            onProgressChanged: progress => {
              props.onUploadStatusChange?.({
                type: 'uploading',
                progress,
                file: localFile,
              });
            },
          });

          props.onUploadStatusChange?.({
            type: 'uploaded',
            file: localFile,
            uri,
          });

          commitFiles(
            [
              {
                source: 'remote',
                uri,
                filename: localFile.filename,
                size: localFile.size,
                type: localFile.type,
              },
            ],
            [],
          );
        } catch (uploadError) {
          props.onUploadStatusChange?.({
            type: 'error',
            file: localFile,
            error: uploadError,
          });
          throw uploadError;
        }
      } catch (error) {
        onError?.(error);
      } finally {
        // Reset the input so the same file can be re-picked.
        setInputKey(prev => prev + 1);
      }
    },
    [commitFiles, onError, props, validateFile],
  );

  return (
    <>
      {children({
        value: props.value,
        uploadedFile: uploadedFiles?.[0],
        uploadedFiles,
        onClick,
      })}
      <input
        ref={inputRef}
        key={inputKey}
        type="file"
        multiple={props.multiple}
        accept={
          supportedFormats
            ? Object.values(supportedFormats).flat().join(',')
            : undefined
        }
        className="hidden"
        onChange={onFileChange}
      />
    </>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Format a size in MB as a human-readable string. Inlined here so we
 * don't pull in `filesize` for one call site. Mirrors PopshopRN's
 * binary (jedec) units: `"512 MB"`, `"1 GB"`, …
 */
function formatSize(maxSizeMB: number): string {
  if (maxSizeMB < 1024) return `${maxSizeMB} MB`;
  const gb = maxSizeMB / 1024;
  return Number.isInteger(gb) ? `${gb} GB` : `${gb.toFixed(1)} GB`;
}
